/**
 * Route Loader для Kernel
 * Завантажує маршрути додатку з підтримкою як SPA, так і Framework режимів
 * Інтеграція з ResourceManager для автогенерації маршрутів ресурсів
 */

import { IConfigLoader } from '@/Core/Contracts/Loaders/IConfigLoader'
import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'
import type { IGeneratedRoute } from '@/Core/Contracts/UI/Resource'
import { BaseLoader } from './BaseLoader'
import { ResourceKernelLoader } from './ResourceKernelLoader'

// Імпорт системи трекінгу для дебагу
let loaderProfiler: any = null
try {
    // Динамічний імпорт щоб уникнути циклічних залежностей
    import('../../Components/Debug/useLoaderProfiler').then((module) => {
        loaderProfiler = module.loaderProfiler
    })
} catch (error) {
    // Ігноруємо помилки, якщо Debug компоненти недоступні
}

export interface RouteInfo {
    path: string
    filePath: string
    config?: IPageConfig
    componentName: string
    isResourceGenerated?: boolean // Прапорець для автогенерованих маршрутів
    key?: string // Унікальний ключ роуту
    section?: string // Назва секції
    container?: string // Назва контейнера
    group?: 'ship' | 'container' | 'resource' // Група маршруту
    template?: string // Використовуваний шаблон
    isIndex?: boolean // Прапорець для index маршрутів
}

export interface RouteLoaderResult {
    routes: RouteInfo[]
    config: {
        ssr: boolean
        prerender: boolean
    }
}

export class RouteKernelLoader extends BaseLoader {
    private routes: RouteInfo[] = []
    private resourceLoader?: ResourceKernelLoader

    constructor() {
        const loaderConfig: IConfigLoader = {
            name: 'routes',
            priority: 8,
            clientSafe: true,
            dependencies: ['config', 'resources'],
        }
        super(loaderConfig)
    }

    /**
     * Встановлює посилання на ResourceKernelLoader для інтеграції
     */
    public setResourceLoader(resourceLoader: ResourceKernelLoader): void {
        this.resourceLoader = resourceLoader
    }

    async load(): Promise<RouteLoaderResult> {
        const startTime = performance.now()

        // Відстеження початку завантаження
        loaderProfiler?.trackStart('routes', { message: 'Початок сканування маршрутів' })

        try {
            // Очищаємо масив маршрутів перед скануванням
            this.routes = []

            loaderProfiler?.trackProgress('routes', { stage: 'scanning', progress: 25 })
            
            // PARALLEL SCANNING: Scan Ship and Container pages in parallel
            await Promise.all([
                this.scanShipPages(),
                this.scanContainerPages(),
            ])

            // Інтегруємо автогенеровані маршрути ресурсів
            loaderProfiler?.trackProgress('routes', { stage: 'resource-routes', progress: 50 })
            await this.integrateResourceRoutes()

            loaderProfiler?.trackProgress('routes', { stage: 'processing', progress: 75 })
            const result: RouteLoaderResult = {
                routes: this.routes,
                config: this.getRouterConfig(),
            }

            const duration = performance.now() - startTime

            // Відстеження успішного завершення
            loaderProfiler?.trackSuccess(
                'routes',
                {
                    routeCount: this.routes.length,
                    routes: this.routes.map((r) => ({ path: r.path, component: r.componentName })),
                },
                duration
            )

            return result
        } catch (error) {
            const duration = performance.now() - startTime
            this.log('Помилка завантаження маршрутів: ' + error, 'error')

            // Відстеження помилки
            loaderProfiler?.trackError('routes', error instanceof Error ? error.message : String(error), duration)

            const fallback = this.getFallbackRoutes()
            return fallback
        }
    }

    private async integrateResourceRoutes(): Promise<void> {
        if (!this.resourceLoader) {
            return
        }

        try {
            await this.resourceLoader.load()

            const generatedRoutes = this.resourceLoader.getGeneratedRoutes()
            
            for (const generatedRoute of generatedRoutes) {
                const routeInfo = this.convertGeneratedRouteToRouteInfo(generatedRoute)

                // Перевіряємо пріоритет: якщо є Page з таким же ключем, виключаємо автогенерований ресурс
                // Перевіряємо як Ship Pages, так і Container Pages
                // Примітка: переопределяються тільки списки (mode: 'list'), карточки обробляються через templates
                const hasPageWithSameKey = this.routes.some(
                    (existingRoute) => 
                        (existingRoute.group === 'ship' || existingRoute.group === 'container') && 
                        existingRoute.key === routeInfo.key
                )

                if (hasPageWithSameKey) {
                    // Логуємо переопределення для відладки
                    this.log(`Resource route "${routeInfo.key}" overridden by custom Page`, 'info')
                    continue
                }

                // Перевіряємо на дублювання шляхів
                const isDuplicate = this.routes.some((existingRoute) => existingRoute.path === routeInfo.path)

                if (!isDuplicate) {
                    this.routes.push(routeInfo)
                }
            }
        } catch (error) {
            this.log('Помилка інтеграції маршрутів ресурсів: ' + error, 'warn')
        }
    }

    /**
     * Конвертує GeneratedRoute в RouteInfo
     * Тільки list та view мають роути
     */
    private convertGeneratedRouteToRouteInfo(generatedRoute: IGeneratedRoute): RouteInfo {
        // Формуємо ключ згідно вимог:
        // - для list: просто "Test" (без .list)
        // - для view: "Test.view"
        // Create та edit НЕ мають роутів
        const resourceKey = generatedRoute.resourceName.replace(/Resource$/, '')

        let routeKey: string
        if (generatedRoute.mode === 'list') {
            routeKey = resourceKey // Test замість Test.list
        } else {
            routeKey = `${resourceKey}.${generatedRoute.mode}`
        }

        return {
            path: generatedRoute.path,
            filePath: generatedRoute.componentPath,
            componentName: 'UniversalResourcePage', // Використовуємо універсальний компонент
            isResourceGenerated: true,
            key: routeKey,
            group: 'resource',
            template: 'default',
            config: {
                title: `${generatedRoute.resourceName} - ${this.capitalizeFirst(generatedRoute.mode)}`,
                template: 'default',
                resourceKey: resourceKey,
            },
        }
    }

    /**
     * Генерує віртуальний шлях до компоненту для автогенерованого роуту
     * @deprecated Currently unused, kept for potential future use
     */
    /*
    private generateVirtualComponentPath(generatedRoute: IGeneratedRoute): string {
        return `virtual:resource/${generatedRoute.resourceName}/${this.capitalizeFirst(generatedRoute.mode)}`
    }
    */

    private capitalizeFirst(str: string): string {
        return str.charAt(0).toUpperCase() + str.slice(1)
    }

    private async scanShipPages(): Promise<void> {
        try {
            // Виправляємо шлях для сканування Ship сторінок
            const shipPages = import.meta.glob('../../../Ship/Pages/*Page.{jsx,tsx}', {
                eager: false,
            })
            
            for (const [filePath, moduleLoader] of Object.entries(shipPages)) {
                try {
                    const fileName = filePath.split('/').pop()!
                    const routePath = this.generatePathFromFilename(fileName)
                    const componentName = this.getComponentName(fileName)

                    // Намагаємось завантажити модуль для отримання конфігурації
                    let pageConfig: IPageConfig | undefined
                    try {
                        const module = (await (moduleLoader as any)()) as any
                        
                        pageConfig = module.pageConfig
                        
                    } catch (error) {
                        this.log(`Не вдалося завантажити конфігурацію для ${fileName}: ${error}`, 'warn')
                    }
                    
                    // Генеруємо ключ для Ship сторінки
                    const routeKey = pageConfig?.key || this.generateRouteKey(undefined, undefined, componentName)

                    // Визначаємо layout
                    const defaultLayout = this.getDefaultLayout(routePath)
                    const layout = pageConfig?.layout || defaultLayout

                    // Перевіряємо, чи це IndexPage
                    const isIndexPage =
                        componentName === 'IndexPage' || fileName === 'IndexPage.tsx' || fileName === 'IndexPage.jsx'
                    const isHomePage = componentName === 'HomePage'

                    const route: RouteInfo = {
                        path: pageConfig?.route ? this.processDynamicParams(pageConfig.route) : routePath,
                        filePath: filePath.replace('../../../', ''),
                        config: {
                            ...pageConfig,
                            layout: layout,
                            title: pageConfig?.title || componentName.replace('Page', ''),
                        },
                        componentName,
                        key: routeKey,
                        group: 'ship',
                        template: pageConfig?.template || layout,
                        isIndex: isIndexPage || isHomePage,
                    }

                    // Перевіряємо на дублювання маршрутів
                    const isDuplicate = this.routes.some(
                        (existingRoute) =>
                            existingRoute.path === route.path && existingRoute.filePath === route.filePath
                    )

                    if (!isDuplicate) {
                        this.routes.push(route)
                    }
                } catch (fileError) {
                    this.log(`Помилка обробки Ship файлу ${filePath}: ${fileError}`, 'error')
                }
            }
        } catch (error) {
            this.log('Помилка сканування Ship сторінок: ' + error, 'warn')
        }
    }

    private async scanContainerPages(): Promise<void> {
        try {
            // Виправляємо шлях для сканування Container сторінок
            const containerPages = import.meta.glob('../../../Containers/**/Pages/*Page.{jsx,tsx}', { eager: false })
            
            for (const [filePath, moduleLoader] of Object.entries(containerPages)) {
                const pathParts = filePath.split('/')
                const fileName = pathParts.pop()!

                // Знаходимо секцію і контейнер у шляху
                const containerIndex = pathParts.findIndex((part) => part.includes('Container'))
                const sectionIndex = pathParts.findIndex((part) => part.includes('Section'))

                if (containerIndex === -1 || sectionIndex === -1) {
                    this.log(`Неправильна структура шляху: ${filePath}`, 'warn')
                    continue
                }

                const sectionName = pathParts[sectionIndex]
                const containerName = pathParts[containerIndex]

                // Null-safety validation
                if (!sectionName || !containerName) {
                    this.log(`Missing section or container name in path: ${filePath}`, 'warn')
                    continue
                }

                const routePath = this.generateContainerPath(sectionName, containerName, fileName)
                const componentName = this.getComponentName(fileName)

                // Намагаємось завантажити модуль для отримання конфігурації
                let pageConfig: IPageConfig | undefined
                try {
                    const module = (await (moduleLoader as any)()) as any
                    pageConfig = module.pageConfig || module.config
                } catch (error) {
                    this.log(`Не вдалося завантажити конфігурацію для ${fileName}: ${error}`, 'warn')
                }
                
                // Перевіряємо, чи це переопределення ресурсу
                const resourceOverride = this.detectResourcePageOverride(componentName, sectionName, containerName)
                
                // Генеруємо ключ для Container сторінки
                let routeKey: string
                if (pageConfig?.key) {
                    routeKey = pageConfig.key
                } else if (resourceOverride.isResourceOverride && resourceOverride.key) {
                    // Використовуємо ключ ресурсу для переопределення
                    routeKey = resourceOverride.key
                } else {
                    routeKey = this.generateRouteKey(sectionName, containerName, componentName)
                }

                // Визначаємо layout
                const defaultLayout = this.getDefaultLayout(routePath)
                const layout = pageConfig?.layout || defaultLayout

                // Перевіряємо, чи це IndexPage
                const isIndexPage =
                    componentName === 'IndexPage' || fileName === 'IndexPage.tsx' || fileName === 'IndexPage.jsx'

                const route: RouteInfo = {
                    path: pageConfig?.route ? this.processDynamicParams(pageConfig.route) : routePath,
                    filePath: filePath.replace('../../../', ''),
                    config: {
                        ...pageConfig,
                        layout: layout,
                        title: pageConfig?.title || componentName.replace('Page', ''),
                    },
                    componentName,
                    key: routeKey,
                    section: this.transformNameToPath(sectionName),
                    container: this.transformNameToPath(containerName),
                    group: 'container',
                    template: pageConfig?.template || layout,
                    isIndex: isIndexPage,
                    // Додаємо прапорець для переопределення
                    isResourceGenerated: false,
                }

                // Перевіряємо на дублювання маршрутів
                const isDuplicate = this.routes.some(
                    (existingRoute) => existingRoute.path === route.path && existingRoute.filePath === route.filePath
                )

                if (!isDuplicate) {
                    this.routes.push(route)
                }
            }
        } catch (error) {
            this.log('Помилка сканування Container сторінок: ' + error, 'warn')
        }
    }
    private generatePathFromFilename(fileName: string): string {
        const baseName = fileName.replace(/\.(jsx|tsx)$/, '').replace(/Page$/, '')

        if (baseName === 'Home' || baseName === 'Index') {
            return '/'
        }

        return (
            '/' +
            baseName
                .replace(/([a-z])([A-Z])/g, '$1-$2') // Правильна трансформація CamelCase
                .toLowerCase()
                .replace(/^-/, '')
        )
    }

    private generateContainerPath(sectionName: string, containerName: string, fileName: string): string {
        // Отримуємо налаштування INCLUDE_CONTAINERS_IN_ROUTES
        const includeContainers = this.getEnvVar('INCLUDE_CONTAINERS_IN_ROUTES') === 'true'

        // Трансформуємо назви секції
        const cleanSectionName = this.transformNameToPath(sectionName)

        // Генеруємо шлях для сторінки з імені файлу
        const pagePath = this.generatePathFromFilename(fileName)
        const pageName = pagePath.substring(1) // прибираємо початковий /

        // Будуємо фінальний шлях
        const pathParts: string[] = []

        // Завжди включаємо секцію
        pathParts.push(cleanSectionName)

        // Включаємо контейнер якщо налаштування включене
        if (includeContainers) {
            const cleanContainerName = this.transformNameToPath(containerName)
            pathParts.push(cleanContainerName)
        }

        // Додаємо сторінку, якщо це не головна сторінка контейнера/секції
        if (pageName) {
            // Для генерації правильного шляху сторінки
            // ClientListPage -> client-list (а не просто list)
            pathParts.push(pageName)
        }

        // Обробляємо динамічні параметри
        const processedParts = pathParts.map((part) => this.processDynamicParams(part))

        return '/' + processedParts.join('/')
    }

    /**
     * Покращена трансформація назв з підтримкою довгих складних слів
     * WarehouseStorageSection -> warehouse-storage
     * ClientContainer -> client
     * OurWarehousePage -> our-warehouse
     */
    private transformNameToPath(name: string): string {
        return (
            name
                // Прибираємо суфікси Section, Container, Page
                .replace(/(Section|Container|Page)$/g, '')
                // Розбиваємо CamelCase на слова
                .replace(/([a-z])([A-Z])/g, '$1-$2')
                // Перетворюємо на нижній регістр
                .toLowerCase()
                // Прибираємо початковий дефіс
                .replace(/^-/, '')
        )
    }

    /**
     * Визначення layout за замовчуванням на основі шляху
     */
    private getDefaultLayout(path: string): string {
        // Якщо шлях починається з /public, використовуємо PublicLayout
        if (path.startsWith('/public')) {
            return 'public'
        }

        // Для інших шляхів використовуємо DefaultLayout
        return 'default'
    }

    /**
     * Визначає, чи є сторінка переопределенням списку ресурсу і генерує відповідний ключ
     * Приклади:
     * - TruckPage -> { isResourceOverride: true, resourceName: 'Truck', mode: 'list', key: 'Vehicle.Truck' }
     * - TruckCreatePage -> { isResourceOverride: false } (карточки обробляються через templates)
     * - CustomPage -> { isResourceOverride: false }
     * 
     * Примітка: Карточки (create/view/edit) переопределяються через templates у ресурсах,
     * тому на рівні роутів переопределяємо тільки сторінки списків.
     */
    private detectResourcePageOverride(
        pageName: string,
        sectionName?: string,
        containerName?: string
    ): {
        isResourceOverride: false
    } | {
        isResourceOverride: true
        resourceName: string
        mode: 'list'
        key: string
    } {
        // Регулярний вираз для визначення паттерну: ResourceName + Page (БЕЗ Create/View/Edit)
        // Наприклад: TruckPage, ContainerPage, але НЕ TruckCreatePage
        const resourcePagePattern = /^([A-Z][a-zA-Z0-9]+)Page$/
        const match = pageName.match(resourcePagePattern)

        if (!match) {
            return { isResourceOverride: false }
        }

        const resourceName = match[1] // 'Truck'

        if (!resourceName) {
            return { isResourceOverride: false }
        }

        // Перевіряємо, чи назва Page відповідає назві Container
        // Якщо так - це переопределення списку ресурсу контейнера
        const cleanContainerName = containerName?.replace(/Container$/, '')
        const isContainerResource = resourceName === cleanContainerName

        if (!isContainerResource) {
            // Це не переопределення ресурсу контейнера
            return { isResourceOverride: false }
        }

        // Генеруємо ключ згідно структури ресурсів (тільки для списку)
        const parts: string[] = []

        if (sectionName) {
            const cleanSection = sectionName.replace(/Section$/, '')
            parts.push(cleanSection)
        }

        parts.push(resourceName)

        const resourceKey = parts.join('.')

        return {
            isResourceOverride: true,
            resourceName,
            mode: 'list' as const,
            key: resourceKey,
        }
    }

    /**
     * Генерація унікального ключа для роуту згідно вимог:
     * 1. Ship/Pages/HomePage.tsx -> Home
     * 2. Ship/Resources/UserResource.tsx -> User
     * 3. Containers/AdminSection/DashboardContainer/Pages/StatsPage.tsx -> Admin.Stats або Admin.Dashboard.Stats
     * 4. Containers/AdminSection/DashboardContainer/Resources/ReportResource.tsx -> Admin.Report або Admin.Dashboard.Report
     */
    private generateRouteKey(section?: string, container?: string, pageName?: string): string {
        const parts: string[] = []

        if (section) {
            // Прибираємо суфікс Section і додаємо назву секції
            const cleanSection = section.replace(/Section$/, '')
            parts.push(cleanSection)
        }

        // Перевіряємо змінну оточення для включення контейнерів у роути
        // Безпечна перевірка для клієнта і сервера
        const env = (import.meta as any).env
        const includeContainers =
            (typeof process !== 'undefined' && process.env?.['INCLUDE_CONTAINERS_IN_ROUTES'] === 'true') ||
            (typeof env !== 'undefined' && env?.['INCLUDE_CONTAINERS_IN_ROUTES'] === 'true')

        if (container && includeContainers) {
            // Прибираємо суфікс Container і додаємо назву контейнера
            const cleanContainer = container.replace(/Container$/, '')
            parts.push(cleanContainer)
        }

        if (pageName) {
            // Прибираємо суфікси Page і Resource
            const cleanPageName = pageName.replace(/(Page|Resource)$/, '')
            parts.push(cleanPageName)
        }

        return parts.join('.')
    }

    /**
     * Обробка динамічних параметрів у назвах папок та роутах
     * [id] -> :id (Next.js style)
     * {id} -> :id (React Router style with curly braces)
     */
    private processDynamicParams(pathSegment: string): string {
        return pathSegment
            .replace(/\[([^\]]+)\]/g, ':$1')  // [id] -> :id
            .replace(/\{([^}]+)\}/g, ':$1')    // {id} -> :id
    }

    /**
     * Отримання імені компоненту з імені файлу
     */
    private getComponentName(fileName: string): string {
        return fileName.replace(/\.(jsx|tsx)$/, '')
    }

    private getRouterConfig(): { ssr: boolean; prerender: boolean } {
        // Визначаємо режим на основі змінних оточення
        const env = (import.meta as any).env
        const framework = env?.['VITE_REACT_ROUTER_MODE'] === 'Framework'

        return {
            ssr: framework,
            prerender: framework,
        }
    }

    private getFallbackRoutes(): RouteLoaderResult {
        return {
            routes: [
                {
                    path: '/',
                    filePath: 'Ship/Pages/HomePage.tsx',
                    componentName: 'HomePage',
                    config: {
                        title: 'Home',
                    },
                },
            ],
            config: {
                ssr: false,
                prerender: false,
            },
        }
    }

    /**
     * Знайти маршрут за ключем
     */
    findRouteByKey(key: string): RouteInfo | undefined {
        return this.routes.find((route) => route.key === key)
    }

    /**
     * Знайти маршрут за шляхом
     */
    findRouteByPath(path: string): RouteInfo | undefined {
        // Спочатку шукаємо точний збіг
        const exactMatch = this.routes.find((route) => route.path === path)
        if (exactMatch) {
            return exactMatch
        }

        // Якщо точного збігу немає, шукаємо серед динамічних роутів
        return this.routes.find((route) => {
            // Перевіряємо чи роут містить динамічні параметри
            if (!route.path.includes(':')) {
                return false
            }

            // Конвертуємо патерн роуту в regex
            // Замінюємо :param на regex групу, що захоплює будь-які символи крім /
            const pattern = route.path
                .replace(/:[^/]+/g, '([^/]+)') // :id -> ([^/]+)
                .replace(/\//g, '\\/') // escape slashes

            const regex = new RegExp(`^${pattern}$`)
            return regex.test(path)
        })
    }

    /**
     * Отримати маршрути за секцією
     */
    getRoutesBySection(section: string): RouteInfo[] {
        return this.routes.filter((route) => route.section === section)
    }

    /**
     * Отримати маршрути за контейнером у секції
     */
    getRoutesByContainerInSection(section: string, container: string): RouteInfo[] {
        return this.routes.filter((route) => route.section === section && route.container === container)
    }

    /**
     * Отримати маршрути для певного контейнера (legacy метод)
     */
    getRoutesByContainer(containerName: string): RouteInfo[] {
        return this.routes.filter((route) => route.filePath.includes(`Containers/${containerName}`))
    }

    /**
     * Отримати всі унікальні секції
     */
    getSections(): string[] {
        const sections = new Set<string>()
        this.routes.forEach((route) => {
            if (route.section) {
                sections.add(route.section)
            }
        })
        return Array.from(sections)
    }

    /**
     * Отримати всі унікальні контейнери у секції
     */
    getContainersInSection(section: string): string[] {
        const containers = new Set<string>()

        this.routes.forEach((route) => {
            if (route.section === section && route.container) {
                containers.add(route.container)
            }
        })

        return Array.from(containers)
    }

    /**
     * Отримати всі унікальні контейнери (legacy метод)
     */
    getContainers(): string[] {
        const containers = new Set<string>()

        this.routes.forEach((route) => {
            const match = route.filePath.match(/Containers\/([^\/]+)/)
            if (match && match[1]) {
                containers.add(match[1])
            }
        })

        return Array.from(containers)
    }

    /**
     * Статичний метод для build-time генерації маршрутів
     * Використовується в route-generator.ts для збірки
     */
    static generateStaticRoutes(): RouteInfo[] {
        // Для build-time режиму скануємо файлову систему синхронно
        if (typeof window === 'undefined' && typeof process !== 'undefined') {
            try {
                return RouteKernelLoader.scanFileSystemRoutes()
            } catch (error) {
                console.warn('Помилка сканування файлової системи:', error)
            }
        }

        // Fallback маршрути
        return []
    }

    /**
     * Синхронне сканування файлової системи для build-time
     */
    static scanFileSystemRoutes(): RouteInfo[] {
        const fs = require('fs')
        const path = require('path')
        const routes: RouteInfo[] = []

        // Допоміжні методи для build-time
        const transformNameToPath = (name: string): string => {
            return name
                .replace(/(Section|Container|Page)$/g, '')
                .replace(/([a-z])([A-Z])/g, '$1-$2')
                .toLowerCase()
                .replace(/^-/, '')
        }

        const getDefaultLayout = (path: string): string => {
            if (path.startsWith('/public')) {
                return 'PublicLayout'
            }
            return 'DefaultLayout'
        }

        const generateRouteKey = (section?: string, container?: string, pageName?: string): string => {
            const parts: string[] = []

            if (section) {
                // Прибираємо суфікс Section і додаємо назву секції
                const cleanSection = section.replace(/Section$/, '')
                parts.push(cleanSection)
            }

            // Перевіряємо змінну оточення для включення контейнерів у роути
            // Безпечна перевірка для клієнта і сервера
            const env = (import.meta as any).env
            const includeContainers =
                (typeof process !== 'undefined' && process.env?.['INCLUDE_CONTAINERS_IN_ROUTES'] === 'true') ||
                (typeof env !== 'undefined' && env?.['INCLUDE_CONTAINERS_IN_ROUTES'] === 'true')

            if (container && includeContainers) {
                // Прибираємо суфікс Container і додаємо назву контейнера
                const cleanContainer = container.replace(/Container$/, '')
                parts.push(cleanContainer)
            }

            if (pageName) {
                // Прибираємо суфікси Page і Resource
                const cleanPageName = pageName.replace(/(Page|Resource)$/, '')
                parts.push(cleanPageName)
            }

            return parts.join('.')
        }

        // Скануємо Ship Pages
        try {
            const shipPagesDir = path.join(process.cwd(), 'src/Ship/Pages')
            if (fs.existsSync(shipPagesDir)) {
                const files = fs
                    .readdirSync(shipPagesDir)
                    .filter((file: string) => file.endsWith('.tsx') || file.endsWith('.jsx'))

                for (const file of files) {
                    const componentName = file.replace(/\.(tsx|jsx)$/, '')
                    const pageName = componentName.replace(/Page$/, '')

                    let routePath: string
                    if (componentName === 'IndexPage') {
                        routePath = '/'
                    } else if (componentName === 'HomePage') {
                        routePath = '/'
                    } else {
                        routePath = '/' + transformNameToPath(componentName)
                    }

                    const routeKey = generateRouteKey(undefined, undefined, componentName)
                    const layout = getDefaultLayout(routePath)

                    // Перевіряємо, чи це IndexPage
                    const isIndexPage = componentName === 'IndexPage' || componentName === 'HomePage'

                    routes.push({
                        path: routePath,
                        filePath: `Ship/Pages/${file}`,
                        componentName,
                        key: routeKey,
                        group: 'ship',
                        template: layout,
                        isIndex: isIndexPage,
                        config: {
                            title: pageName,
                            layout: layout,
                        },
                    })
                }
            }
        } catch (error) {
            console.warn('Помилка сканування Ship Pages:', error)
        }

        // Скануємо Container Pages
        try {
            const containersDir = path.join(process.cwd(), 'src/Containers')
            if (fs.existsSync(containersDir)) {
                const scanContainerDir = (dirPath: string, relativePath: string = '') => {
                    const items = fs.readdirSync(dirPath)

                    for (const item of items) {
                        const fullPath = path.join(dirPath, item)
                        const newRelativePath = relativePath ? `${relativePath}/${item}` : item

                        if (fs.statSync(fullPath).isDirectory()) {
                            if (item === 'Pages') {
                                // Знайдена папка Pages, скануємо файли
                                const pageFiles = fs
                                    .readdirSync(fullPath)
                                    .filter(
                                        (file: string) =>
                                            (file.endsWith('.tsx') || file.endsWith('.jsx')) && file.includes('Page')
                                    )

                                for (const pageFile of pageFiles) {
                                    // Витягуємо секцію і контейнер зі шляху
                                    const pathParts = newRelativePath.split('/')
                                    const sectionIndex = pathParts.findIndex((part: string) => part.includes('Section'))
                                    const containerIndex = pathParts.findIndex((part: string) =>
                                        part.includes('Container')
                                    )

                                    if (sectionIndex !== -1 && containerIndex !== -1) {
                                        const sectionName = pathParts[sectionIndex]
                                        const containerName = pathParts[containerIndex]
                                        const componentName = pageFile.replace(/\.(tsx|jsx)$/, '')
                                        const pageName = componentName.replace(/Page$/, '')

                                        // Перевіряємо змінну оточення
                                        const includeContainers = process.env['INCLUDE_CONTAINERS_IN_ROUTES'] === 'true'

                                        // Будуємо шлях
                                        const pathSegments: string[] = []
                                        const sectionPath = transformNameToPath(sectionName)
                                        pathSegments.push(sectionPath)

                                        if (includeContainers) {
                                            pathSegments.push(transformNameToPath(containerName))
                                        }

                                        // Додаємо сторінку, якщо це не головна
                                        const pageSegment = transformNameToPath(pageName)
                                        if (pageSegment) {
                                            // Для генерації правильного шляху сторінки
                                            // ClientListPage -> client-list (а не просто list)
                                            pathSegments.push(pageSegment)
                                        }

                                        const routePath = '/' + pathSegments.join('/')
                                        const routeKey = generateRouteKey(sectionName, containerName, componentName)
                                        const layout = getDefaultLayout(routePath)

                                        // Перевіряємо, чи це IndexPage
                                        const isIndexPage = componentName === 'IndexPage'

                                        routes.push({
                                            path: routePath,
                                            filePath: `Containers/${newRelativePath}/${pageFile}`,
                                            componentName,
                                            key: routeKey,
                                            section: transformNameToPath(sectionName),
                                            container: transformNameToPath(containerName),
                                            group: 'container',
                                            template: layout,
                                            isIndex: isIndexPage,
                                            config: {
                                                title: pageName,
                                                layout: layout,
                                            },
                                        })
                                    }
                                }
                            } else {
                                // Продовжуємо рекурсивний пошук
                                scanContainerDir(fullPath, newRelativePath)
                            }
                        }
                    }
                }

                scanContainerDir(containersDir)
            }
        } catch (error) {
            console.warn('Помилка сканування Container Pages:', error)
        }

        // Сканируем Container Resources
        try {
            const containersDir = path.join(process.cwd(), 'src/Containers')
            if (fs.existsSync(containersDir)) {
                const scanContainerResources = (dirPath: string, relativePath = '') => {
                    const items = fs.readdirSync(dirPath)

                    for (const item of items) {
                        const fullPath = path.join(dirPath, item)
                        const newRelativePath = relativePath ? `${relativePath}/${item}` : item

                        if (fs.lstatSync(fullPath).isDirectory()) {
                            // Если это папка Resources
                            if (item === 'Resources') {
                                const resourceFiles = fs
                                    .readdirSync(fullPath)
                                    .filter(
                                        (file: string) =>
                                            (file.endsWith('.ts') ||
                                                file.endsWith('.tsx') ||
                                                file.endsWith('.js') ||
                                                file.endsWith('.jsx')) &&
                                            file !== 'index.ts'
                                    )

                                for (const resourceFile of resourceFiles) {
                                    try {
                                        // Извлекаем названия секции и контейнера из пути
                                        const pathParts = relativePath.split('/')
                                        const sectionName = pathParts[0] // например: AdminSection
                                        const containerName = pathParts[1] // например: DashboardContainer

                                        if (sectionName && containerName) {
                                            const componentName = resourceFile.replace(/\.(tsx?|jsx?)$/, '')
                                            
                                            // Log found resources for debugging
                                            console.debug(`Found container resource: ${sectionName}/${containerName}/${componentName}`)
                                        }
                                    } catch (error) {
                                        console.warn(`Помилка обробки Container Resource файлу ${resourceFile}:`, error)
                                    }
                                }
                            } else {
                                // Продолжаем рекурсивный поиск
                                scanContainerResources(fullPath, newRelativePath)
                            }
                        }
                    }
                }

                scanContainerResources(containersDir)
            }
        } catch (error) {
            console.warn('Помилка сканування Container Resources:', error)
        }

        // Сканируем Ship Resources
        try {
            const resourcesDir = path.join(process.cwd(), 'src/Ship/Resources')
            if (fs.existsSync(resourcesDir)) {
                const resourceFiles = fs
                    .readdirSync(resourcesDir)
                    .filter((file: string) => (file.endsWith('.ts') || file.endsWith('.js')) && file !== 'index.ts')

                for (const resourceFile of resourceFiles) {
                    try {
                        // Генерируем ключ из названия файла (Ship/Resources/UserResource.tsx -> User)
                        const componentName = resourceFile.replace(/\.(tsx?|jsx?)$/, '')
                        const resourceKey = generateRouteKey(undefined, undefined, componentName)

                        // Читаем файл ресурса
                        const resourcePath = path.join(resourcesDir, resourceFile)
                        const resourceContent = fs.readFileSync(resourcePath, 'utf8')

                        // Ищем pageUrl в файле
                        const pageUrlMatch = resourceContent.match(/pageUrl\s*:\s*['"`]([^'"`]+)['"`]/)
                        const titleMatch = resourceContent.match(/title\s*:\s*['"`]([^'"`]+)['"`]/)

                        if (pageUrlMatch) {
                            const pageUrl = pageUrlMatch[1]
                            const title = titleMatch ? titleMatch[1] : resourceKey

                            // Проверяем наличие routeConfig
                            const routeConfigMatch = resourceContent.match(/routeConfig\s*:\s*\{([^}]+)\}/)
                            let routeConfigContent = ''

                            if (routeConfigMatch) {
                                routeConfigContent = routeConfigMatch[1]
                            }

                            // Функція для перевірки значення в routeConfig (за замовчуванням true)
                            const getRouteConfigValue = (key: string): boolean => {
                                if (!routeConfigMatch) {
                                    // Якщо routeConfig взагалі не вказано, всі роути true
                                    return true
                                }

                                // Шукаємо явне значення в routeConfig
                                const explicitFalsePattern = new RegExp(`${key}\\s*:\\s*false`)
                                const explicitTruePattern = new RegExp(`${key}\\s*:\\s*true`)

                                if (explicitFalsePattern.test(routeConfigContent)) {
                                    return false
                                }

                                if (explicitTruePattern.test(routeConfigContent)) {
                                    return true
                                }

                                // Якщо ключ не знайдено, за замовчуванням true
                                return true
                            }

                            // Генерируем CRUD роуты з перевіркою за замовчуванням
                            if (getRouteConfigValue('list')) {
                                routes.push({
                                    path: pageUrl,
                                    filePath: `Core/Components/Resource/UniversalResourcePage.tsx`,
                                    componentName: 'UniversalResourcePage',
                                    key: `${resourceKey}`,
                                    group: 'resource',
                                    template: 'DefaultLayout',
                                    isResourceGenerated: true,
                                    config: {
                                        title: `${title} - Список`,
                                        layout: 'DefaultLayout',
                                        resourceKey: resourceKey,
                                    },
                                })
                            }

                            if (getRouteConfigValue('create')) {
                                routes.push({
                                    path: `${pageUrl}/create`,
                                    filePath: `Core/Components/Resource/UniversalResourcePage.tsx`,
                                    componentName: 'UniversalResourcePage',
                                    key: `${resourceKey}.create`,
                                    group: 'resource',
                                    template: 'DefaultLayout',
                                    isResourceGenerated: true,
                                    config: {
                                        title: `${title} - Створити`,
                                        layout: 'DefaultLayout',
                                        resourceKey: resourceKey,
                                    },
                                })
                            }

                            if (getRouteConfigValue('view')) {
                                routes.push({
                                    path: `${pageUrl}/:id`,
                                    filePath: `Core/Components/Resource/UniversalResourcePage.tsx`,
                                    componentName: 'UniversalResourcePage',
                                    key: `${resourceKey}.view`,
                                    group: 'resource',
                                    template: 'DefaultLayout',
                                    isResourceGenerated: true,
                                    config: {
                                        title: `${title} - Перегляд`,
                                        layout: 'DefaultLayout',
                                        resourceKey: resourceKey,
                                    },
                                })
                            }

                            if (getRouteConfigValue('edit')) {
                                routes.push({
                                    path: `${pageUrl}/:id/edit`,
                                    filePath: `Core/Components/Resource/UniversalResourcePage.tsx`,
                                    componentName: 'UniversalResourcePage',
                                    key: `${resourceKey}.edit`,
                                    group: 'resource',
                                    template: 'DefaultLayout',
                                    isResourceGenerated: true,
                                    config: {
                                        title: `${title} - Редагувати`,
                                        layout: 'DefaultLayout',
                                        resourceKey: resourceKey,
                                    },
                                })
                            }
                        }
                    } catch (fileError) {
                        console.warn(`Помилка обробки ресурсу ${resourceFile}:`, fileError)
                    }
                }
            }
        } catch (error) {
            console.warn('Помилка сканування ресурсів:', error)
        }

        return routes
    }

    /**
     * Отримати всі завантажені роути
     */
    public getAllRoutes(): RouteInfo[] {
        return this.routes
    }

    /**
     * Статичний метод для build-time конфігурації
     */
    static getStaticRouterConfig(): { ssr: boolean; prerender: boolean } {
        // Перевіряємо змінні оточення на build-time
        if (typeof process !== 'undefined' && process.env) {
            const framework = process.env['REACT_ROUTER_MODE'] === 'Framework'
            return {
                ssr: framework,
                prerender: framework,
            }
        }

        return {
            ssr: false,
            prerender: false,
        }
    }
}
